import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useGame } from '../context/GameContext';
import { normalizeTag } from '../game/profile';
import { CURSES } from '../game/curses';
import { ArrowLeft, Coins, Target, Trophy, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bounties() {
  const { goToMenu, playerName, profile, refreshProfile, beginClassSelect } = useGame();
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPost, setShowPost] = useState(false);
  const [postCode, setPostCode] = useState('');
  const [postPrize, setPostPrize] = useState(100);
  const [postMsg, setPostMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bounties`);
      setBounties(r.data || []);
    } catch (e) { setBounties([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const tokens = profile?.slop_tokens || 0;

  const postBounty = async () => {
    setErr('');
    const tag = normalizeTag(playerName);
    if (!tag) { setErr('SET A TAG FIRST'); return; }
    try {
      await axios.post(`${API}/bounties`, {
        run_code: postCode.trim().toUpperCase(),
        posted_by: tag,
        prize_tokens: parseInt(postPrize, 10) || 100,
        message: postMsg.slice(0, 200),
      });
      setShowPost(false); setPostCode(''); setPostPrize(100); setPostMsg('');
      await refreshProfile();
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'FAILED');
    }
  };

  const attempt = (runCode) => {
    beginClassSelect(runCode, null, runCode);
  };

  return (
    <div className="min-h-screen w-full px-4 md:px-6 py-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="bounties-back-button">
          <ArrowLeft size={16}/> BACK
        </button>
        <h1 className="font-display text-3xl sm:text-5xl font-black uppercase text-[color:var(--neon)] flex items-center gap-2">
          <Target /> SLOP BOUNTIES
        </h1>
        <div className="brutal-border-pink px-3 py-2 flex items-center gap-2">
          <Coins size={14} className="text-[color:var(--pink)]"/>
          <span className="font-display font-black text-lg text-[color:var(--pink)]" data-testid="bounties-tokens">{tokens}</span>
        </div>
      </div>

      <div className="mt-3 text-center text-sm uppercase tracking-widest text-[color:var(--text-dim)]">
        post cursed run codes with a token prize. first goon to beat it claims the bag.
      </div>

      <div className="mt-4 flex justify-end">
        <button className="brutal-btn-pink brutal-btn text-sm" onClick={() => setShowPost(v => !v)} data-testid="bounties-post-toggle">
          <Plus size={14}/> POST BOUNTY
        </button>
      </div>

      {showPost && (
        <div className="mt-3 brutal-border-pink p-4 bg-[#0a0a0a]" data-testid="bounties-post-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input value={postCode} onChange={e => setPostCode(e.target.value.toUpperCase())} placeholder="RUN CODE" maxLength={12}
              className="bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--pink)] px-3 py-2 font-mono uppercase outline-none tracking-widest"
              data-testid="bounty-code-input"/>
            <input value={postPrize} onChange={e => setPostPrize(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="PRIZE (tokens)"
              className="bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--pink)] px-3 py-2 font-mono outline-none"
              data-testid="bounty-prize-input"/>
            <input value={postMsg} onChange={e => setPostMsg(e.target.value.slice(0, 60))} placeholder="TRASH TALK (60 chars)"
              className="bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--pink)] px-3 py-2 font-mono outline-none"
              data-testid="bounty-msg-input"/>
          </div>
          <button className="brutal-btn-pink brutal-btn text-sm mt-3" onClick={postBounty} disabled={!postCode.trim() || parseInt(postPrize,10) < 10} data-testid="bounty-submit-button">
            POST FOR {postPrize} TOKENS
          </button>
          {err && <div className="mt-2 text-[color:var(--red)] text-xs uppercase" data-testid="bounty-error">{err}</div>}
        </div>
      )}

      <div className="mt-6 space-y-2" data-testid="bounties-list">
        {loading && <div className="text-[color:var(--text-dim)] text-center py-6">Loading...</div>}
        {!loading && bounties.length === 0 && (
          <div className="text-center py-10 brutal-border-dim text-[color:var(--text-dim)] uppercase text-sm">NO OPEN BOUNTIES · POST THE FIRST</div>
        )}
        {bounties.map((b, i) => {
          const curse = b.curse_id ? CURSES[b.curse_id] : null;
          return (
            <div key={b.id} className="brutal-border p-3 bg-[#0a0a0a] flex items-center gap-4 flex-wrap"
              data-testid={`bounty-${i}`}>
              <div className="font-display text-3xl font-black text-[color:var(--pink)] tracking-widest">{b.run_code}</div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] uppercase text-[color:var(--text-dim)]">POSTED BY <span className="text-white font-bold">{b.posted_by}</span></div>
                {b.message && <div className="text-sm text-[color:var(--text)] italic mt-1">"{b.message}"</div>}
                {curse && <div className="text-xs mt-1" style={{color: curse.color}}>{curse.art} {curse.name}</div>}
              </div>
              <div className="brutal-border-pink px-3 py-2 text-center">
                <div className="text-[10px] uppercase text-[color:var(--text-dim)]">PRIZE</div>
                <div className="font-display text-2xl font-black text-[color:var(--pink)]">{b.prize_tokens} 🪙</div>
              </div>
              <button className="brutal-btn text-sm" onClick={() => attempt(b.run_code)} data-testid={`bounty-attempt-${i}`}>
                <Trophy size={14}/> CLAIM
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
